import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CustomStandardModal } from '@app/components/Modal';
import { resetOrders } from '@app/redux/billingInfo';
import { getBuyMoreOffers, selectBuyMore, setIsCheckout } from '@app/redux/buyMore';

import BrowseContent from './BrowseContent';
import BrowseError from './BrowseError';
import BrowseNoOffers from './BrowseNoOffers';
import { ErrorEnum } from '../utils/types';

import styles from './styles.module.scss';

type BrowseProps = {
  isModalOpen: boolean;
  handleClose: (isSuccess: boolean, error?: ErrorEnum) => void;
};

const BrowseContainer = (props: BrowseProps): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation('BuyMore');

  const { handleClose, isModalOpen } = props;
  const { offersError, noOffers, selectedProduct, loading } = useSelector(selectBuyMore);

  useEffect(() => {
    if (isModalOpen) {
      dispatch(getBuyMoreOffers());
    }
  }, [dispatch, isModalOpen]);

  const handleModalClose = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  const handleConfirm = useCallback(() => {
    if (!selectedProduct) {
      return;
    }

    dispatch(setIsCheckout(true));
    dispatch(resetOrders());
  }, [selectedProduct]);

  if (offersError)
    return (
      <BrowseError
        isModalOpen={isModalOpen}
        handleClose={handleModalClose}
        refetchProducts={() => dispatch(getBuyMoreOffers())}
      />
    );

  if (noOffers) {
    return <BrowseNoOffers isModalOpen={isModalOpen} handleClose={handleModalClose} />;
  }

  return (
    <CustomStandardModal
      size="medium"
      isOpen={isModalOpen}
      header={undefined}
      actions={{
        primary: {
          label: t('Browse.buttons.confirmPurchase'),
          onClick: handleConfirm,
          disabled: loading || !selectedProduct,
        },
        secondary: {
          label: t('Browse.buttons.cancel'),
          onClick: handleModalClose,
          'data-cy': 'buy-more-cancel-button',
        },
      }}
      onClose={handleModalClose}
      surfaceProps={{ className: styles['customActions'] }}
    >
      <BrowseContent />
    </CustomStandardModal>
  );
};

export default BrowseContainer;
